import * as React from 'react'
import { useId } from 'react'
import { useController, Control, FieldValues, FieldPath } from 'react-hook-form'
import * as SelectPrimitive from '@radix-ui/react-select'
import clsx from 'clsx'
import { isRHFProps } from '@/core/utils/form'
import { useInputGroupPosition } from '@/common/components/input-group'
import { Icon } from '@/common/components/icon'
import * as styles from './Select.css'

// =============================================================================
// Types
// =============================================================================

export type SelectOption = {
  value: string
  label: string
  disabled?: boolean
}

export type SelectOptionGroup = {
  label: string
  options: SelectOption[]
}

type SelectBaseProps = {
  /** Label text (required) */
  label: string
  /** Options to display */
  options: (SelectOption | SelectOptionGroup)[]
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Show error styling (red border/label) */
  error?: boolean
  /** Whether the field is optional (shows "(Optional)" indicator) */
  optional?: boolean
  /** Whether to show the "(Optional)" indicator when optional is true. Defaults to true. */
  showOptional?: boolean
  /** Placeholder text when no value selected */
  placeholder?: string
  /** Icon element to show on the left */
  leftIcon?: React.ReactNode
  /** Additional class name for the wrapper */
  className?: string
  /** Whether the select is disabled */
  disabled?: boolean
  /** Label for the clear option (shown when optional and has value). Defaults to "None" */
  clearLabel?: string
  /**
   * Minimum width for the dropdown.
   * Can be a number (pixels) or string (e.g., '200px', '100%').
   * By default, dropdown matches trigger width.
   */
  dropdownMinWidth?: number | string
  /**
   * Horizontal alignment of the dropdown relative to the trigger.
   * @default 'start'
   */
  dropdownAlign?: 'start' | 'end'
}

// Props for standalone usage (no react-hook-form)
type SelectStandaloneProps = SelectBaseProps & {
  /** Current value */
  value?: string
  /** Called when value changes */
  onValueChange?: (value: string) => void
  /** Called when select loses focus */
  onBlur?: () => void
}

// Props for react-hook-form usage
type SelectRHFProps<T extends FieldValues, N extends FieldPath<T>> = SelectBaseProps & {
  /** Field name for react-hook-form */
  name: N
  /** Control object from useForm */
  control: Control<T>
}

export type SelectProps<T extends FieldValues = FieldValues, N extends FieldPath<T> = FieldPath<T>> =
  | SelectStandaloneProps
  | SelectRHFProps<T, N>

// Type guard to check if option is a group
function isOptionGroup(option: SelectOption | SelectOptionGroup): option is SelectOptionGroup {
  return 'options' in option
}

// Radix reserves empty string for "no selection", so we use a special value for the clear option
const CLEAR_VALUE = '__clear__'

// =============================================================================
// Component
// =============================================================================

function SelectInner<T extends FieldValues, N extends FieldPath<T>>(
  props: SelectProps<T, N>,
  forwardedRef: React.ForwardedRef<HTMLButtonElement>
) {
  const {
    label,
    options,
    size = 'md',
    error: errorProp,
    optional,
    showOptional = true,
    placeholder,
    leftIcon,
    className,
    disabled,
    clearLabel = 'None',
    dropdownMinWidth,
    dropdownAlign = 'start',
  } = props

  // Generate unique ID for the trigger
  const triggerId = useId()

  // InputGroup integration
  const groupPosition = useInputGroupPosition()

  // Track mount state to disable transitions on initial render
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => {
    requestAnimationFrame(() => setMounted(true))
  }, [])

  // RHF integration
  let rhfField: {
    value?: string
    onChange?: (value: string) => void
    onBlur?: () => void
    ref?: React.Ref<HTMLButtonElement>
    name?: string
  } | null = null
  let fieldError = false

  if (isRHFProps(props)) {
    const { name, control } = props as SelectRHFProps<T, N>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { field, fieldState } = useController({ name, control })
    rhfField = {
      value: field.value ?? '',
      onChange: field.onChange,
      onBlur: field.onBlur,
      ref: field.ref,
      name: field.name,
    }
    fieldError = !!fieldState.error
  }

  // Determine values based on mode
  const value = rhfField?.value ?? ('value' in props ? props.value : undefined)
  const rawOnValueChange = rhfField?.onChange ?? ('onValueChange' in props ? props.onValueChange : undefined)
  const onBlur = rhfField?.onBlur ?? ('onBlur' in props ? props.onBlur : undefined)

  // Convert CLEAR_VALUE to empty string for the consumer
  const onValueChange = rawOnValueChange
    ? (newValue: string) => rawOnValueChange(newValue === CLEAR_VALUE ? '' : newValue)
    : undefined

  // Determine if we have an error (prop or RHF)
  const hasError = errorProp ?? fieldError

  // Determine if label should be floated
  const hasValue = !!value
  const hasPlaceholder = !!placeholder
  const shouldFloatLabel = hasPlaceholder || hasValue

  // Handle blur
  const handleOpenChange = (open: boolean) => {
    if (!open && onBlur) {
      onBlur()
    }
  }

  return (
    <div className={clsx(
      styles.wrapper({ size, error: hasError, disabled }),
      groupPosition === 'start' && styles.groupWrapperStart,
      groupPosition === 'middle' && styles.groupWrapperMiddle,
      !mounted && styles.noTransition,
      className
    )}>
      <SelectPrimitive.Root
        value={value}
        onValueChange={onValueChange}
        onOpenChange={handleOpenChange}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          ref={forwardedRef}
          id={triggerId}
          className={clsx(
            styles.trigger,
            groupPosition === 'start' && styles.groupPositionStart,
            groupPosition === 'middle' && styles.groupPositionMiddle,
            groupPosition === 'end' && styles.groupPositionEnd,
            hasError && styles.triggerError,
            disabled && styles.triggerDisabled
          )}
          aria-label={optional ? `${label} (optional)` : label}
          aria-invalid={hasError || undefined}
          aria-required={!optional || undefined}
        >
          {/* Left icon */}
          {leftIcon && (
            <div className={clsx(styles.leftIcon, disabled && styles.leftIconDisabled)}>
              {leftIcon}
            </div>
          )}

          {/* Content area with value and floating label */}
          <div className={styles.content}>
            {/* Selected value or placeholder */}
            {value ? (
              <span className={clsx(
                styles.value,
                shouldFloatLabel && styles.valueWithFloatedLabel,
                disabled && styles.valueDisabled
              )}>
                {/* Find the label for the current value */}
                {options.flatMap(opt =>
                  isOptionGroup(opt) ? opt.options : [opt]
                ).find(opt => opt.value === value)?.label ?? value}
              </span>
            ) : placeholder ? (
              <span className={styles.placeholder}>
                {placeholder}
              </span>
            ) : null}

            {/* Floating label (visual only, aria-label on trigger handles a11y) */}
            <span
              aria-hidden="true"
              className={clsx(
                styles.label,
                shouldFloatLabel && styles.labelFloated,
                hasError && styles.labelError,
                disabled && styles.labelDisabled
              )}
            >
              {label}
            </span>
          </div>

          {/* Optional indicator (visual only) - positioned over dropdown icon */}
          {optional && showOptional && (
            <span
              aria-hidden="true"
              className={clsx(
                styles.optional,
                shouldFloatLabel && styles.optionalFloated
              )}
            >
              (Optional)
            </span>
          )}

          {/* Dropdown icon */}
          <SelectPrimitive.Icon className={clsx(styles.triggerIcon, disabled && styles.triggerIconDisabled)}>
            <Icon name="expand_more" size={20} />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className={styles.dropdown}
            position="popper"
            sideOffset={4}
            align={dropdownAlign}
            style={dropdownMinWidth ? { minWidth: typeof dropdownMinWidth === 'number' ? `${dropdownMinWidth}px` : dropdownMinWidth } : undefined}
          >
            <SelectPrimitive.ScrollUpButton className={styles.scrollButton}>
              <Icon name="expand_less" size={16} />
            </SelectPrimitive.ScrollUpButton>
            <SelectPrimitive.Viewport className={styles.viewport}>
              {/* Clear option for optional selects */}
              {optional && (
                <SelectPrimitive.Item
                  value={CLEAR_VALUE}
                  className={clsx(styles.item, styles.clearItem)}
                  aria-selected={!hasValue}
                >
                  <SelectPrimitive.ItemText>{clearLabel}</SelectPrimitive.ItemText>
                  {!hasValue && (
                    <span className={styles.itemIndicator}>
                      <Icon name="check" size={16} />
                    </span>
                  )}
                </SelectPrimitive.Item>
              )}
              {options.map((option, index) => {
                if (isOptionGroup(option)) {
                  return (
                    <SelectPrimitive.Group key={option.label}>
                      <SelectPrimitive.Label className={styles.groupLabel}>
                        {option.label}
                      </SelectPrimitive.Label>
                      {option.options.map(opt => (
                        <SelectPrimitive.Item
                          key={opt.value}
                          value={opt.value}
                          disabled={opt.disabled}
                          className={styles.item}
                        >
                          <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
                          <SelectPrimitive.ItemIndicator className={styles.itemIndicator}>
                            <Icon name="check" size={16} />
                          </SelectPrimitive.ItemIndicator>
                        </SelectPrimitive.Item>
                      ))}
                      {index < options.length - 1 && (
                        <SelectPrimitive.Separator className={styles.separator} />
                      )}
                    </SelectPrimitive.Group>
                  )
                }
                return (
                  <SelectPrimitive.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={styles.item}
                  >
                    <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                    <SelectPrimitive.ItemIndicator className={styles.itemIndicator}>
                      <Icon name="check" size={16} />
                    </SelectPrimitive.ItemIndicator>
                  </SelectPrimitive.Item>
                )
              })}
            </SelectPrimitive.Viewport>
            <SelectPrimitive.ScrollDownButton className={styles.scrollButton}>
              <Icon name="expand_more" size={16} />
            </SelectPrimitive.ScrollDownButton>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  )
}

// ForwardRef wrapper with generic support
export const Select = React.forwardRef(SelectInner) as <
  T extends FieldValues = FieldValues,
  N extends FieldPath<T> = FieldPath<T>
>(
  props: SelectProps<T, N> & { ref?: React.ForwardedRef<HTMLButtonElement> }
) => React.ReactElement

// Display name for debugging
(Select as React.FC).displayName = 'Select'
